import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef
} from "react";
import PropTypes from "prop-types";
import { t } from "@lingui/macro";

import { i18n } from "base/i18n";

import { Icon } from "@/common/components/primitives/icon";
import { FLOW_ICON_SIZES } from "@/datadisplay/flow_icon/flowicon";
import { FlowIconWrapper } from "@/datadisplay/flow_icon/iconwrapper";
import { EditableSingleLine } from "@/inputs/editableinput";
import { Link } from "@/navigation/link";
import {
  ScrollableTabs,
  SCROLLABLE_TABS_HIGHLIGHT_TYPES
} from "@/navigation/tab";
import { CardTooltip, CARD_TOOLTIP_POSITIONS } from "@/feedback/card_tooltip";

import styles from "./flowcontainer.css";

import InfoWithCircleIcon from "./InfoWithCircle.svg";

export const FlowContainer = forwardRef((props, ref) => {
  const {
    title,
    onTitleChange,
    homeRoute,
    icon,
    children,
    extraTabRenderer = () => {},
    showDescription = true,
    description = "",
    onDescriptionSave,
    readOnly = false,
    subTitle,
    onChangeIcon,
    flowId,
    flowType,
    staticIcon = false
  } = props;

  const [currentTitle, setCurrentTitle] = useState(null);
  const titleRef = useRef();
  const titleInputRef = useRef(null);

  useEffect(
    function titleEffect() {
      setCurrentTitle(title);
      titleRef.current = title;
    },
    [title]
  );

  useEffect(
    function currentTitleEffect() {
      if (!currentTitle || !currentTitle.trim()) {
        setCurrentTitle(titleRef.current);
      } else {
        titleRef.current = currentTitle;
      }
    },
    [currentTitle]
  );

  const slots = React.Children.map(children, (child) => child);

  const memberPanel = slots.find(
    (child) => child.type === FlowContainer.Members
  );
  const actionsPanel = slots.find(
    (child) => child.type === FlowContainer.Actions
  );
  const editPanel = slots.find(
    (child) => child.type === FlowContainer.EditIcon
  );
  const favoritePanel = slots.find(
    (child) => child.type === FlowContainer.Favorite
  );
  const body = slots.find((child) => child.type === FlowContainer.Body);
  const tab = slots.find((child) => child.type === FlowContainer.Tabs);
  const breadcrumb = slots.find(
    (child) => child.type === FlowContainer.Breadcrumb
  );

  useImperativeHandle(ref, () => {
    return {
      setTitle: (title) => {
        titleInputRef.current.setValue(title);
      }
    };
  });

  return (
    <div className={styles.flowcontainer}>
      <div className={styles.header}>
        <div
          className={`${styles.icon} ${staticIcon ? styles.staticIcon : ""}`}
        >
          <Link route={homeRoute}>
            {staticIcon ? (
              icon
            ) : (
              <FlowIconWrapper
                flowId={flowId}
                flowType={flowType}
                size={FLOW_ICON_SIZES.SIZE54}
                onUpdate={onChangeIcon}
                icon={icon}
                showBackground={true}
                editable={!readOnly}
              />
            )}
          </Link>
          <div className={styles.editIcon}>{editPanel}</div>
        </div>
        <div className={styles.space16} />
        <div className={styles.maincontainer}>
          <div className={styles.titleactioncontainer}>
            <div className={styles.titleContainer}>
              <div className={styles.titleInputContainer}>
                <EditableSingleLine
                  ref={titleInputRef}
                  value={currentTitle}
                  className={`${styles.title} h5-600`}
                  onSave={(title) => {
                    setCurrentTitle(title);
                    onTitleChange && onTitleChange(title);
                  }}
                  readOnly={readOnly}
                />
              </div>
              {showDescription && ((readOnly && description) || !readOnly) && (
                <div className={styles.infoIconContainer}>
                  <div className={styles.infoIcon}>
                    <CardTooltip
                      className={styles.descriptionContainer}
                      title={i18n._(t`Description`)}
                      content={description}
                      position={CARD_TOOLTIP_POSITIONS.BOTTOM}
                      onSave={(data) => {
                        onDescriptionSave(data);
                      }}
                      emptyText={i18n._(t`No description is added yet.`)}
                      canEdit={!readOnly}
                      targetRenderer={() => (
                        <Icon clickable={true}>
                          <InfoWithCircleIcon />
                        </Icon>
                      )}
                    />
                  </div>
                </div>
              )}
              {favoritePanel && (
                <div className={styles.starIconContainer}>{favoritePanel}</div>
              )}
              <div className={styles.members}>{memberPanel}</div>
            </div>
            <div className={styles.spacer} />
          </div>
          <div className={styles.titlecount}>{subTitle}</div>
          {tab && (
            <div className={styles.tabscontainer}>
              {tab}
              {extraTabRenderer && extraTabRenderer()}
            </div>
          )}
          {breadcrumb && (
            <div className={styles.breadcrumbcontainer}>{breadcrumb}</div>
          )}
        </div>
        <div className={styles.actions}>{actionsPanel}</div>
      </div>
      <div className={styles.flowdivider} />
      <div className={styles.body}>{body}</div>
    </div>
  );
});

FlowContainer.propTypes = {
  title: PropTypes.string,
  onTitleChange: PropTypes.func,
  homeRoute: PropTypes.string,
  icon: PropTypes.node,
  children: PropTypes.node,
  extraTabRenderer: PropTypes.func,
  showDescription: PropTypes.bool,
  description: PropTypes.string,
  onDescriptionSave: PropTypes.func,
  readOnly: PropTypes.bool,
  subTitle: PropTypes.node,
  onChangeIcon: PropTypes.func,
  flowId: PropTypes.string,
  flowType: PropTypes.string,
  staticIcon: PropTypes.bool
};

function FlowHeaderMemberPanel(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowHeaderMemberPanel.propTypes = {
  children: PropTypes.node
};

FlowContainer.Members = FlowHeaderMemberPanel;
FlowContainer.Members.displayName = "FlowContainer.Members";

function FlowHeaderActionsPanel(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowHeaderActionsPanel.propTypes = {
  children: PropTypes.node
};

FlowContainer.Actions = FlowHeaderActionsPanel;
FlowContainer.Actions.displayName = "FlowContainer.Actions";

function FlowBody(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowBody.propTypes = {
  children: PropTypes.node
};

FlowContainer.Body = FlowBody;
FlowContainer.Body.displayName = "FlowContainer.Body";

function FlowEditIcon(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowEditIcon.propTypes = {
  children: PropTypes.node
};

FlowContainer.EditIcon = FlowEditIcon;
FlowContainer.EditIcon.displayName = "FlowContainer.EditIcon";

function FlowFavorite(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowFavorite.propTypes = {
  children: PropTypes.node
};

FlowContainer.Favorite = FlowFavorite;
FlowContainer.Favorite.displayName = "FlowContainer.Favorite";

function FlowTab(props) {
  const { children, activeTabId, onChange } = props;
  const slots = React.Children.map(children, (child) => child);
  const tabItems = slots.filter((child) => child.type === FlowContainer.Tab);
  const leftAction = slots.find(
    (child) => child.type === FlowContainer.Tabs.LeftAction
  );
  const rightAction = slots.find(
    (child) => child.type === FlowContainer.Tabs.RightAction
  );
  return (
    <React.Fragment>
      <ScrollableTabs
        className={styles.tab}
        activeTabId={activeTabId}
        onChange={onChange}
        highlight={SCROLLABLE_TABS_HIGHLIGHT_TYPES.GRAY}
      >
        {leftAction && (
          <ScrollableTabs.LeftAction>{leftAction}</ScrollableTabs.LeftAction>
        )}
        {rightAction && (
          <ScrollableTabs.RightAction>{rightAction}</ScrollableTabs.RightAction>
        )}
        {React.Children.map(tabItems, (tab, index) => {
          const { title, number, tabId, icon, maxNumber, badgeType } =
            tab.props;
          return (
            <ScrollableTabs.Tab
              key={index}
              title={title}
              number={number}
              maxNumber={maxNumber}
              icon={icon}
              badgeType={badgeType}
              tabId={tabId}
            />
          );
        })}
      </ScrollableTabs>
    </React.Fragment>
  );
}
FlowTab.propTypes = {
  children: PropTypes.node,
  activeTabId: PropTypes.string,
  onChange: PropTypes.func,
  showMoreTabsText: PropTypes.bool,
  moreTabsText: PropTypes.string,
  maxVisibleTabs: PropTypes.number
};

FlowContainer.Tabs = FlowTab;
FlowContainer.Tabs.displayName = "FlowContainer.Tabs";

function FlowTabItem(props) {
  const { children, ...restProps } = props;
  return <React.Fragment {...restProps}>{children}</React.Fragment>;
}
FlowTabItem.propTypes = {
  children: PropTypes.node,
  tabId: PropTypes.string
};

FlowContainer.Tab = FlowTabItem;
FlowContainer.Tab.displayName = "FlowContainer.Tab";

function FlowTabMenu(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowTabMenu.propTypes = {
  children: PropTypes.node
};

FlowContainer.TabMenu = FlowTabMenu;

function LeftAction(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}

LeftAction.propTypes = {
  children: PropTypes.node
};

FlowContainer.Tabs.LeftAction = LeftAction;

function RightAction(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}

RightAction.propTypes = {
  children: PropTypes.node
};

FlowContainer.Tabs.RightAction = RightAction;

function FlowBreadcrumb(props) {
  return <React.Fragment>{props.children}</React.Fragment>;
}
FlowBreadcrumb.propTypes = {
  children: PropTypes.node
};

FlowContainer.Breadcrumb = FlowBreadcrumb;

export * from "./minimalflow";
